import React, { useState } from 'react'
import Drawer from '@mui/material/Drawer'
import {CONVERTIBLE_CURRENCIES, themeColors, radius} from '../../constants'
import Star from '../FormElements/Star/Star'
import CurrencyRow from '../CurrencyRow/CurrencyRow'

// Inline currency picker shown on the 金額 row. Tapping the pill opens a
// searchable bottom sheet listing ALL currencies in two sections: 常用 (the book
// settlement currency + the book's favorites) on top, then 其他. Each 其他 row
// has a ☆ button to promote it into 常用 (persisted on the book), and favorites
// have a ★ to demote. Tapping a row selects that currency.
const CurrencyDropdown = ({ value, bookCurrency, favorites = [], onChange, onToggleFavorite }) => {
  const [open, setOpen] = useState(false)
  const [query, setQuery] = useState('')

  const metaOf = (code) => {
    const m = CONVERTIBLE_CURRENCIES.find((c) => c.code === code)
    return m || { code, flag: '🏳️', label: code }
  }

  // 常用 = settlement currency first, then the book's favorites (deduped).
  const favoriteCodes = [bookCurrency, ...favorites.filter((c) => c && c !== bookCurrency)]
    .filter((c, i, arr) => c && arr.indexOf(c) === i)
  const otherList = CONVERTIBLE_CURRENCIES.filter((c) => !favoriteCodes.includes(c.code))

  const selected = metaOf(value || bookCurrency)
  const isForeign = !!value && value !== bookCurrency

  const pick = (code) => {
    if (onChange) { onChange(code) }
    setOpen(false)
    setQuery('')
  }

  const toggleFav = (e, code) => {
    e.stopPropagation()
    if (onToggleFavorite) { onToggleFavorite(code) }
  }

  const q = query.trim().toLowerCase()
  const matches = (c) => c.code.toLowerCase().includes(q) || (c.label || '').toLowerCase().includes(q)

  const Row = ({ code, removable, addable }) => {
    const o = metaOf(code)
    return (
      <button type="button" onClick={() => pick(code)} style={{ ...styles.row, ...(code === selected.code ? styles.rowActive : {}) }}>
        <CurrencyRow flag={o.flag} code={o.code} label={`${o.label}${code === bookCurrency ? '（本帳本）' : ''}`} />
        {code === selected.code ? <span style={styles.check}>✓</span> : null}
        {onToggleFavorite && code !== bookCurrency && (removable || addable)
          ? <Star solid={removable} color={themeColors.gold700} clicked={(e) => toggleFav(e, code)} />
          : null}
      </button>
    )
  }

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        style={{ ...styles.pill, ...styles.pillDefault }}
      >
        <span style={{ marginRight: 4 }}>{selected.flag}</span>
        <span>{selected.label}</span>
        <span style={{ marginLeft: 4 }}>▾</span>
      </button>

      <Drawer
        anchor="bottom"
        open={open}
        onClose={() => { setOpen(false); setQuery('') }}
        PaperProps={{ sx: { borderRadius: '16px 16px 0 0', height: '88vh' } }}
      >
        <div style={styles.sheet}>
          <div style={styles.title}>選擇幣別</div>
          <div style={styles.searchWrap}>
            <span style={{ marginRight: 8 }}>🔍</span>
            <input
              style={styles.search}
              type="text"
              placeholder="搜尋幣別（USD、美元…）"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
            />
          </div>

          <div style={styles.list}>
            {q ? (
              // Flat filtered results across everything.
              (() => {
                const hits = [...favoriteCodes.map(metaOf), ...otherList].filter(matches)
                return hits.length === 0
                  ? <div style={styles.empty}>找不到符合的幣別</div>
                  : hits.map((o) => (
                      <Row key={o.code} code={o.code}
                        removable={favoriteCodes.includes(o.code)}
                        addable={!favoriteCodes.includes(o.code)} />
                    ))
              })()
            ) : (
              <>
                <div style={styles.sectionHead}>常用</div>
                {favoriteCodes.map((code) => (
                  <Row key={code} code={code} removable={code !== bookCurrency} addable={false} />
                ))}
                <div style={styles.sectionHead}>其他</div>
                {otherList.map((o) => (
                  <Row key={o.code} code={o.code} removable={false} addable />
                ))}
              </>
            )}
          </div>
        </div>
      </Drawer>
    </>
  )
}

export default CurrencyDropdown

const styles = {
  pill: {
    display: 'inline-flex', alignItems: 'center', border: 'none', borderRadius: radius.pill,
    padding: '6px 12px', fontSize: '14px', fontWeight: 600, cursor: 'pointer', whiteSpace: 'nowrap',
  },
  pillDefault: { background: themeColors.gray400, color: themeColors.gray700 },
  sheet: { padding: '16px', height: '100%', boxSizing: 'border-box', display: 'flex', flexDirection: 'column' },
  title: { fontSize: '16px', fontWeight: 600, textAlign: 'center', paddingBottom: '12px', flex: '0 0 auto' },
  searchWrap: { display: 'flex', alignItems: 'center', background: themeColors.gray300, borderRadius: radius.md, padding: '8px 12px', flex: '0 0 auto' },
  search: { border: 'none', outline: 'none', background: 'transparent', flex: 1, fontSize: '14px' },
  list: { overflowY: 'auto', marginTop: '8px', flex: '1 1 auto', minHeight: 0 },
  sectionHead: { fontSize: '12px', fontWeight: 600, color: themeColors.gray600, padding: '12px 4px 4px' },
  row: {
    display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%',
    background: 'transparent', border: 'none', padding: '12px 4px', cursor: 'pointer',
  },
  rowActive: { background: 'rgba(7, 105, 77, 0.08)' },
  flag: { fontSize: '22px', paddingRight: '12px' },
  code: { fontWeight: 600, paddingRight: '10px', color: themeColors.gray900 },
  label: { color: themeColors.gray700, flex: 1, textAlign: 'left' },
  check: { color: themeColors.green500, fontWeight: 600, paddingRight: '10px' },
  star: { fontSize: '18px', color: themeColors.gold700, padding: '0 4px' },
}
